// General
import React, { useState, useContext, useEffect } from "react";
// UI Components
import { Modal } from "react-bootstrap";
import {
  TextField,
  Autocomplete,
  Button,
  Card,
  InputAdornment,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
// Formik
import { Formik, Form, Field } from "formik";
// Contexts
import { ServiceContext } from "../../data/services/ServiceContext";
import { CustomerContext } from "../../data/customer/CustomerContext";
import { OfferContext } from "../../data/offer/OfferContext";
// Modals & Components
import CustomerAddModal from "../customer/CustomerAddModal";
import CapitalizedFormikField from "../../components/CapitalizedFormikField";

// OfferUpdateModal bileşeni
function OfferUpdateModal({ show, handleClose, offer }) {
  // Contextlerden gerekli fonksiyon ve verileri çekiyoruz
  const { updateOffer } = useContext(OfferContext);
  const { services } = useContext(ServiceContext);
  const { customers, addCustomer } = useContext(CustomerContext);

  // Local state tanımlamaları
  const [showCustomerModal, setShowCustomerModal] = useState(false); // Yeni müşteri modalı
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Seçilen müşteri
  const [selectedServices, setSelectedServices] = useState([]); // Seçilen servisler

  // Teklif durum seçenekleri
  const STATUS_OPTIONS = [
    { value: "", label: "Seçiniz" },
    { value: "Beklemede", label: "Beklemede" },
    { value: "Onaylandı", label: "Onaylandı" },
  ];

  // Teklif geldiğinde müşteri ve servisleri local state'e set et
  useEffect(() => {
    if (offer) {
      const customer = customers.find((c) => c.id === offer.customerId) || null;
      setSelectedCustomer(customer);
      setSelectedServices(
        (offer.items || []).map((item) => ({
          ...item,
          vatRate: item.vatRate ?? offer.vatRate ?? 18, // KDV oranı default
        }))
      );
    }
  }, [offer, customers]);

  // Formik başlangıç değerleri
  const initialValues = {
    vatRate: offer?.vatRate || 18,
    plannedDate: offer?.plannedDate || "",
    note: offer?.note || "",
    fullName: selectedCustomer?.fullName || "",
    phone1: selectedCustomer?.phone1 || "",
    phone2: selectedCustomer?.phone2 || "",
    email: selectedCustomer?.email || "",
    customerType: selectedCustomer?.customerType || "Bireysel",
    tcNo: selectedCustomer?.tcNo || "",
    vergiNo: selectedCustomer?.vergiNo || "",
    vergiDairesi: selectedCustomer?.vergiDairesi || "",
    city: selectedCustomer?.city || "",
    district: selectedCustomer?.district || "",
    address: selectedCustomer?.address || "",
    status: offer?.status || "",
  };

  // Yeni boş servis ekleme fonksiyonu
  const handleAddEmptyService = () => {
    setSelectedServices((prev) => [
      ...prev,
      { id: Date.now(), name: "", quantity: 1, price: 0, vatRate: initialValues.vatRate },
    ]);
  };

  // Servis silme fonksiyonu
  const handleRemoveService = (id) => {
    setSelectedServices((prev) => prev.filter((s) => s.id !== id));
  };

  // Toplam tutar hesaplama fonksiyonu
  const calculateTotal = (vatRate) => {
    return selectedServices.reduce(
      (sum, s) => sum + s.quantity * s.price * (1 + (s.vatRate ?? vatRate) / 100),
      0
    );
  };

  return (
    <>
      {/* Modal Başlangıcı */}
      <Modal show={show} onHide={handleClose} size="xl" centered>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values) => {
            // Eğer müşteri yok ve formda isim yoksa submit etme
            if (!selectedCustomer && !values.fullName) return;

            // Müşteri ID belirleme / yeni müşteri ekleme
            let customerId;
            if (selectedCustomer) {
              customerId = selectedCustomer.id;
            } else {
              const newCustomer = {
                fullName: values.fullName,
                phone1: values.phone1,
                phone2: values.phone2,
                email: values.email,
                customerType: values.customerType,
                tcNo: values.tcNo,
                vergiNo: values.vergiNo,
                vergiDairesi: values.vergiDairesi,
                city: values.city,
                district: values.district,
                address: values.address,
              };
              addCustomer(newCustomer);
              customerId = customers.length + 1; // Yeni ID
            }

            // Güncellenmiş teklif objesi
            const updatedOffer = {
              ...offer,
              customerId,
              plannedDate: values.plannedDate,
              vatRate: values.vatRate,
              note: values.note,
              status: values.status,
              items: selectedServices.map((s) => ({ ...s, vatRate: s.vatRate ?? values.vatRate })),
            };

            updateOffer(updatedOffer); // Teklifi güncelle
            handleClose(); // Modalı kapat
          }}
        >
          {({ values, handleChange, setFieldValue }) => (
            <Form>
              <Modal.Header closeButton>
                <Modal.Title>Teklif Güncelle</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <div className="d-flex flex-column flex-lg-row gap-4">
                  {/* SOL: Müşteri Bilgileri */}
                  <div style={{ flex: 0.4 , maxHeight:400 }}>
                    <h5>Müşteri Bilgileri</h5>
                    {/* Müşteri seçme ve ekleme */}
                    <div className="d-flex gap-1 my-2" style={{ alignItems: "center" }}>
                      <Autocomplete
                        options={customers}
                        getOptionLabel={(c) => c.fullName}
                        value={selectedCustomer}
                        onChange={(_, newValue) => {
                          setSelectedCustomer(newValue);
                          if (newValue) {
                            Object.keys(values).forEach((key) => {
                              if (newValue[key] !== undefined) setFieldValue(key, newValue[key]);
                            });
                          }
                        }}
                        sx={{ flex: 1 }}
                        renderInput={(params) => (
                          <TextField {...params} label="Kayıtlı Müşteri Seç" size="small" />
                        )}
                      />
                      <Button variant="contained" size="small" onClick={() => setShowCustomerModal(true)}>
                        Ekle
                      </Button>
                    </div>

                    {/* Servis Seçimi */}
                    <Autocomplete
                      options={services.filter(
                        (s) => !selectedCustomer || s.customerId === selectedCustomer.id
                      )}
                      getOptionLabel={(s) => `${s.brand} ${s.device} ${s.deviceModel || ""}`.trim()}
                      value={selectedServices[0] || null} // Tek servis seçimi
                      onChange={(_, selected) => {
                        if (selected) {
                          setSelectedServices([{
                            ...selected,
                            quantity: selectedServices[0]?.quantity || 1,
                            price: selectedServices[0]?.price || selected.price || 0,
                            vatRate: selectedServices[0]?.vatRate || values.vatRate,
                          }]);
                        } else {
                          setSelectedServices([]);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Servis Seç"
                          placeholder="Servisi seçin"
                          size="small"
                          className="my-1"
                        />
                      )}
                    />

                    {/* Durum */}
                    <div className="row mb-2 mt-3">
                      <div className="col" >
                        <CapitalizedFormikField
                          select
                          name="status"
                          label="Durum"
                          variant="outlined"
                          size="small"
                          fullWidth
                          SelectProps={{ native: true }}
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </CapitalizedFormikField>
                      </div>
                    </div>

                    {/* Planlanan teslim tarihi */}
                    <Field
                      as={TextField}
                      type="date"
                      name="plannedDate"
                      label="Planlanan Teslim Tarihi"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      className="my-1"
                    />

                    {/* Ek not */}
                    <Field
                      as={TextField}
                      name="note"
                      label="Ek Not"
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      className="my-2"
                    />

                    {/* KDV ve toplam */}
                    <div className="d-flex gap-2 my-2 flex-wrap">
                      <TextField
                        label="KDV’siz Toplam"
                        value={calculateTotal(0).toFixed(2)}
                        size="small"
                        InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                        style={{ flex: 1 }}
                      />
                      <TextField
                        label="KDV (%)"
                        name="vatRate"
                        type="number"
                        size="small"
                        value={values.vatRate}
                        onChange={(e) => {
                          const newVat = Number(e.target.value);
                          handleChange(e);
                          setSelectedServices((prev) =>
                            prev.map((s) => ({ ...s, vatRate: newVat }))
                          );
                        }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        style={{ flex: 1 }}
                      />
                      <TextField
                        label="KDV’li Toplam"
                        value={calculateTotal(values.vatRate).toFixed(2)}
                        size="small"
                        InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  {/* SAĞ: Servis / Teklif Detayları */}
                  <div style={{ flex: 0.6 }}>
                    <h5 className="d-flex justify-content-between align-items-center">
                      Servis / Teklif Detayları
                      <Button variant="contained" size="small" onClick={handleAddEmptyService}>
                        Kalem Ekle
                      </Button>
                    </h5>

                    {/* Servis kartları */}
                    <Card variant="outlined" style={{ maxHeight: 350, overflowY: "auto", padding: 10, width: "100%" }}>
                      {selectedServices.map((s) => (
                        <div key={s.id} className="d-flex flex-column flex-md-row gap-2 my-2">
                          <TextField
                            value={s.name}
                            onChange={(e) => {
                              s.name = e.target.value;
                              setSelectedServices([...selectedServices]);
                            }}
                            size="small"
                            fullWidth
                          />
                          <div className="d-flex gap-2 mt-1 flex-md-row flex-row">
                            <TextField
                              label="Adet"
                              type="number"
                              size="small"
                              value={s.quantity}
                              onChange={(e) => {
                                s.quantity = Number(e.target.value);
                                setSelectedServices([...selectedServices]);
                              }}
                              style={{ width: 80 }}
                            />
                            <TextField
                              label="Fiyat"
                              size="small"
                              value={s.price}
                              onChange={(e) => {
                                s.price = Number(e.target.value);
                                setSelectedServices([...selectedServices]);
                              }}
                              InputProps={{ startAdornment: <InputAdornment position="start">₺</InputAdornment> }}
                              style={{ width: 120 }}
                            />
                            <IconButton color="error" size="small" onClick={() => handleRemoveService(s.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        </div>
                      ))}
                    </Card>
                  </div>
                </div>
              </Modal.Body>

              {/* Footer */}
              <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Yeni müşteri ekleme modalı */}
      <CustomerAddModal show={showCustomerModal} handleClose={() => setShowCustomerModal(false)} />
    </>
  );
}

export default OfferUpdateModal;
